import { RunningEvent } from '../types';

// Citations object for reference management
export const CITATIONS = {
  1: {
    title: 'Time to correct the record on the global burden of myalgic encephalomyelitis/chronic fatigue syndrome (ME/CFS)',
    authors: 'Vardaman M, Gilmour S',
    journal: 'Journal of Translational Medicine',
    year: '2025',
    url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC11907965/',
    doi: '10.1186/s12967-025-06281-0'
  },
  2: {
    title: 'Economic Impact Report: Long COVID and ME/CFS',
    authors: 'ME/CFS Research Foundation',
    year: '2024',
    url: 'https://mecfs-research.org/costreport-long-covid-and-mecfs/'
  },
  3: {
    title: 'ME/CFS Funding Report',
    authors: 'ME Research UK',
    year: '2016',
    url: 'https://www.meresearch.org.uk/mecfs-funding-report/'
  },
  4: {
    title: 'Health-related quality of life in patients with chronic fatigue syndrome: an international study',
    authors: 'Nacul LC, Lacerda EM, Pheby D, et al.',
    journal: 'PLOS ONE',
    year: '2011',
    url: 'https://journals.plos.org/plosone/article?id=10.1371/journal.pone.0132421',
    doi: '10.1371/journal.pone.0132421'
  },
  5: {
    title: 'Ärzteblatt Report: Long COVID and ME/CFS costs society 63 billion euros annually',
    authors: 'Deutsches Ärzteblatt',
    year: '2024',
    url: 'https://www.aerzteblatt.de/news/medienbericht-long-covid-und-mecfs-kosten-gesellschaft-63-milliarden-euro-jahrlich-ac6ab36c-b2e4-40a1-8f27-6b52d53dd9e8'
  },
  6: {
    title: 'BMBF Research Funding for ME/CFS',
    authors: 'ME/CFS Research Foundation',
    year: '2023',
    url: 'https://mecfs-research.org/en/bmbf-researchfunding-sep23/'
  },
  7: {
    title: 'Characterization of Post-exertional Malaise in Patients With Myalgic Encephalomyelitis/Chronic Fatigue Syndrome',
    authors: 'Stussman B, Williams A, Snow J, et al.',
    journal: 'Frontiers in Neurology',
    year: '2020',
    url: 'https://www.frontiersin.org/articles/10.3389/fneur.2020.01025/full',
    doi: '10.3389/fneur.2020.01025'
  },
  8: {
    title: 'Long COVID and ME/CFS: A systematic review and meta-analysis',
    authors: 'Multiple authors',
    journal: 'Journal of Infection',
    year: '2024',
    url: 'https://linkinghub.elsevier.com/retrieve/pii/S0163445324002317',
    doi: '10.1016/j.jinf.2024.01.001'
  }
};

export const ME_INFO = {
  title: 'What is M.E.?',
  description: 'Myalgic Encephalomyelitis (M.E.) is a complex, neuroimmunological, chronic illness that affects approx. 71 million people worldwide¹, in Germany alone, approximately 650,000+⁵, with many cases going undiagnosed. \n This severely disabling condition is usually triggered by a viral infection. The exact cause of the disease is poorly understood, but various dysfunctions of the nervous system, endocrine system, immune system and other systems and organs have already been identified. It can leave patients completely bedridden, unable to perform even basic daily activities like eating, speaking, or tolerating light and sound. \n Despite affecting millions worldwide and being one of the most disabling chronic illnesses, M.E. remains largely misunderstood, underfunded, and often dismissed by healthcare providers.',
  keyFacts: {
    title: 'Key Facts',
    description: 'Understanding the fundamental aspects of this complex illness',
    cards: [
      {
        icon: 'People',
        title: 'Global Impact',
        description: 'Affects approximately 71 million people worldwide¹, with many cases going undiagnosed.',
      },
      {
        icon: 'Virus',
        title: 'Viral Triggers',
        description: 'Triggered often by viral infections including COVID-19, Epstein-Barr virus, and other pathogens.',
      },
      {
        icon: 'Health',
        title: 'Severe Disability',
        description: 'Can leave patients completely bedridden, unable to perform basic daily activities.',
      },
    ],
  },
  pemDescription: ' Post-exertional Neuroimmune Exhaustion (PENE) is the cardinal symptom of M.E.⁷ - which means a worsening of symptoms after even minimal physical or mental exertion. This can last for days, weeks, or even months, making it impossible for patients to maintain normal activity levels.',
  whyThisMatters: {
    title: 'Why M.E. Awareness Matters',
    description: 'Understanding M.E. is crucial because it affects millions of people worldwide, yet remains largely misunderstood and underfunded. The reality for people with M.E. is that they are severely limited in their health and additionally receive little understanding for their illness. Despite its severity, M.E. is often not taken seriously by healthcare providers and remains underfunded. By supporting research and raising awareness, we can help improve the lives of those affected and create a future with better treatment options.',
  },
  symptoms: {
    title: 'Symptoms',
    pemTitle: '⚠️ Post-exertional Neuroimmune Exhaustion (PENE) - The Cardinal Symptom',
    otherSymptomsTitle: 'Other Symptoms',
    list: [
      'Muscle weakness',
      'Flu-like exhaustion',
      'Unrefreshing sleep',
      'Pain in muscle, joints or nerves',
      'Malfunctioning of cognitive functions (brain fog)',
      'Orthostatic intolerance (difficulty standing up from a lying or sitting position)',
      'Sensitivity to light, sound, and other stimuli',
    ],
  },
  economicImpact: {
    title: 'Economic Impact',
    description: 'M.E. and Long COVID together have a large economic impact in Germany, costing approximately 63 billion euros annually². Of this total, approximately half is attributable to only M.E. This includes healthcare costs, lost productivity, and the burden on families and caregivers. Similar economic impacts have been found in other countries, with studies showing comparable burdens relative to GDP.',
    source: 'https://mecfs-research.org/costreport-long-covid-and-mecfs/',
    study: {
      id: 'economicImpact',
      title: 'Economic Impact in Germany by Year',
      data: [
        { year: '2022', cost: '58 billion €', gdpImpact: 1.4 },
        { year: '2023', cost: '61 billion €', gdpImpact: 1.5 },
        { year: '2024', cost: '63 billion €', gdpImpact: 1.5 },
        { year: '2025 (estimated)', cost: '65 billion €', gdpImpact: 1.6 },
      ],
      source: 'https://mecfs-research.org/costreport-long-covid-and-mecfs/',
      buttonText: 'View Study',
    },
  },
  researchFunding: {
    title: 'Research Funding Gap',
    description: 'Current public research funding for M.E. is extremely low worldwide. 2006-2016 it has globally been only around 54 million euros³. And since then the the situation has not changed drastically. E.g. Germany currently "only" funds 15 million euros for a 3-year M.E. research program⁶. This stands against the massive economic burden that this illness causes - in Germany alone, M.E. and Long COVID together cost approximately 63 billion euros annually², with M.E. accounting for roughly half. The research funding gap remains enormous, and this lack of funding has severely slowed down progress in understanding the disease and developing effective treatments.',
    source: 'https://www.meresearch.org.uk/mecfs-funding-report/',
    study: {
      id: 'researchFunding',
      title: 'Research Funding Comparison (2006-2016 Data)',
      data: [
        { condition: 'Epilepsy', funding: 1840, unit: 'million €' },
        { condition: 'Rheumatoid Arthritis', funding: 1495, unit: 'million €' },
        { condition: 'Multiple Sclerosis', funding: 1265, unit: 'million €' },
        { condition: 'Attention Deficit Disorder', funding: 608, unit: 'million €' },
        { condition: 'Migraine', funding: 248, unit: 'million €' },
        { condition: 'Ataxia Telangiectasia', funding: 85, unit: 'million €' },
        { condition: 'M.E./CFS', funding: 54, unit: 'million €' },
      ],
      source: 'https://www.meresearch.org.uk/mecfs-funding-report/',
      buttonText: 'View Research',
    },
  },
  mostDisabling: {
    title: 'M.E. - One of the Most Disabling Chronic Illnesses',
    description: 'M.E. is considered one of the most disabling chronic illnesses, with many patients experiencing a quality of life worse than those with multiple sclerosis, cancer, or heart disease⁴. Living with M.E. means living with constant pain and other symtoms. Patients on the severe end of the spectrum are often completely bedridden, unable to care for themselves and describe their life as "Buried alive".',
    source: 'https://mecfs-research.org/researchfunding-munich/',
    study: {
      id: 'healthIndex',
      title: 'Health Index Comparison',
      explanation: 'The EQ-5D 3L is a standardized measure that assesses physical, mental, and social well-being. Scores below 0.5 indicate significant health impairment, while scores above 0.7 represent relatively good health status.',
      data: [
        { condition: 'ME/CFS', score: 0.47 },
        { condition: 'COPD', score: 0.65 },
        { condition: 'Multiple Sclerosis', score: 0.67 },
        { condition: 'Rheumatoid Arthritis', score: 0.69 },
        { condition: 'Lung Cancer', score: 0.69 },
        { condition: 'Stroke', score: 0.71 },
        { condition: 'Diabetes Type II', score: 0.72 },
        { condition: 'Colon Cancer', score: 0.74 },
        { condition: 'General Population', score: 0.85 },
      ],
      source: 'https://journals.plos.org/plosone/article?id=10.1371/journal.pone.0132421',
      buttonText: 'View Study',
    },
  },
  prevalence: {
    title: 'M.E. - A Common Yet Hidden Illness',
    description: 'Despite being largely invisible in public discourse, M.E. is surprisingly common. M.E. affects an estimated ~71 million people worldwide¹, representing approximately 0.9% of the global population. Yet remains largely unrecognized and misunderstood by the general public and healthcare systems and receives little attention and funding.',
    globalStats: {
      title: 'Global Prevalence',
      data: [
        { region: 'Worldwide', cases: '~71 million', percentage: '0.9%' },
        { region: 'United States', cases: '3-4 million', percentage: '1-1.2%' },
        { region: 'Germany', cases: '~650,000', percentage: '0.8%' },
      ],
    },
    underdiagnosis: {
      title: 'The Hidden Prevalence',
      description: 'These numbers likely represent only a fraction of the true prevalence, as M.E. is notoriously underdiagnosed. Many patients are misdiagnosed with depression, anxiety, or other conditions, while others never receive a diagnosis at all. The actual number of people affected could be significantly higher.',
    },
    sources: [
      {
        name: 'Germany - Ärzteblatt Report',
        url: 'https://www.aerzteblatt.de/news/medienbericht-long-covid-und-mecfs-kosten-gesellschaft-63-milliarden-euro-jahrlich-ac6ab36c-b2e4-40a1-8f27-6b52d53dd9e8',
      },
      {
        name: 'US - NIH ME/CFS Research',
        url: 'https://www.nih.gov/advancing-mecfs-research',
      },
      {
        name: 'Global - NIH ME/CFS Study',
        url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC11907965/',
      },
    ],
  },
  longCovid: {
    title: 'Long COVID & M.E.',
    statement: 'Studies indicate that after approximately six months of illness duration, about half of Long COVID patients meet the diagnostic criteria for M.E.⁸.',
    description: 'M.E. is the most severe form of Long COVID, but COVID-19 is just one of many viral triggers for M.E..',
    keyPoints: [
      'M.E. is the most severe form of Long COVID',
      'COVID-19 is just one of many viral triggers',
      'Other triggers include Epstein-Barr virus, enteroviruses, herpes viruses',
      'The underlying disease mechanism is the same regardless of trigger',
      'M.E. has been documented for decades before COVID-19',
    ],
  },
  livingWithME: {
    title: 'What Does Living with M.E. Mean?',
    description: "Living with M.E. means facing a healthcare system that often doesn't understand or believe your illness. Many patients struggle to find doctors who can help, and most treatments are experimental or unavailable.",
    stages: [
      {
        severity: 'Mild',
        description: 'Symptoms already significantly limit daily life, leaving patients with only a fraction of their previous performance level. Can work part-time but must forgo leisure activities and experience severe limitations in social life. Often difficult for others to understand their limitations.',
      },
      {
        severity: 'Moderate',
        description: 'Mostly housebound due to severe symptoms. Unable to work and rely on help for household tasks. Can only remain upright for limited periods and walk only short distances. Mobility aids like wheelchairs can improve quality of life, but insurance often denies coverage.',
      },
      {
        severity: 'Severe',
        description: 'Bedridden most of the time, requiring intensive help for basic daily activities and personal hygiene. Can only concentrate for very short periods and tolerate sensory stimuli (light, noise, touch) for brief durations. Many have no access to medical care as few doctors make house calls.',
      },
      {
        severity: 'Very Severe',
        description: 'Cannot tolerate any sensory stimuli (light, noise, touch) and must lie shielded in darkened, silent rooms. Require full-time care and sometimes need artificial feeding. Among the most severely ill people, yet there is a critical lack of adequately trained care personnel.',
      },
    ],
  },
  impactOnPatients: {
    title: 'Impact on Patients',
    description: 'M.E. affects approximately ~71 million people worldwide¹, with many cases going undiagnosed. The illness can be severely disabling, leaving many patients bedridden or housebound.',
    additionalInfo: 'Many patients face disbelief from healthcare providers, struggle to find appropriate care, and are often misdiagnosed with other conditions. The lack of understanding and research funding means that most treatments are experimental or unavailable.',
    familyImpact: 'Families and caregivers also bear a tremendous burden, both emotionally and financially, as they support loved ones who may be completely bedridden or require constant care.',
  },
};

export const FUNDRAISER_INFO = {
  title: 'Our Mission',
  description: 'We started as three friends. Today more than 300 people wear our shirt — at marathons, trail runs, corporate races and on a lap around the park, in Germany, Austria and across Europe, to raise awareness of M.E. and collect donations.',
  description_short: 'We started as three friends. Today more than 300 people wear our shirt — at marathons, trail runs, corporate races and on a lap around the park, all across Europe, to raise awareness of M.E. and collect donations.',
  vision: 'We want a future where M.E. is recognised early, diagnosed correctly, and where patients get reliable care — and treatments that actually work.',
  personalStory1: 'Every one of them makes visible an illness that affects 71 million people worldwide¹ — and that almost nobody has heard of. We have friends and family living with M.E. who cannot run. So we run for them.',
  personalStory2: "You don't need a personal best. You need a shirt and a run.",
  impact: 'Your support funds research into the causes and treatments of M.E. and helps people living with this often isolating illness.',
  // Homepage specific content
  hero: {
    title: 'Run for M.E.',
    subtitle: 'M.E. is a marathon without a finish line.',
    tagline: 'More than 300 runners across Europe are running for those who cannot. Run with us.',
    buttons: {
      learnAboutME: 'Learn About M.E.',
      viewTimeline: 'View Runs',
      joinUs: 'Run With Us — Get a Shirt',
      supportCause: 'Support Our Cause',
    },
    attribution: {
      photoBy: 'Photo by',
    },
  },
  stats: {
    shirtsValue: '300+',
    shirtsLabel: 'shirts sent out',
    // runsYear splits the current season from already-run events on the runs page
    runsYear: '2026',
    runsLabel: 'runs since 2025',
    countriesLabel: 'countries',
    priceValue: '€14',
    priceLabel: 'at cost price',
  },
  howItWorks: {
    title: 'How it works',
    steps: [
      {
        number: '1',
        title: 'Tell us you are in',
        description: 'Fill in the form: which run, which size. Marathon, 5k or a lap around the park — all of it counts.',
      },
      {
        number: '2',
        title: 'Your shirt arrives by post',
        description: '€14 — €10 production, €2 shipping, €2 materials. We make nothing on it.',
      },
      {
        number: '3',
        title: 'You run, we celebrate you',
        description: 'Your run goes on our list. Your photo — if you like — goes on Instagram.',
      },
    ],
    button: 'Sign up now',
  },
  cards: {
    marathon: {
      title: 'Get a shirt & run',
      description: '€14 at cost price, shipping and flyers included. We make no profit on it — we just want it seen out there.',
    },
    research: {
      title: 'Find your run',
      description: 'Runs all across Europe are on our list — or bring your own. Every distance counts.',
    },
    awareness: {
      title: 'Donate',
      description: 'Your donation goes via betterplace.org straight to M.E. research and care for the most severely ill patients. Tax receipt included.',
    },
  },
  awareness: {
    title: 'Make M.E. visible',
    subtitle: 'Tell five people about M.E. That is already a start.',
    description: 'M.E. affects millions of people — and still almost nobody has heard of it. That is exactly the problem: what nobody knows about does not get researched, diagnosed or treated. Every conversation, every shared post and every shirt on a race course makes the illness a little more visible.',
  },
  donation: {
    title: 'Donate',
    subtitle: 'Your donation goes straight to M.E. research and patient care.',
    impact: "We collect through betterplace.org, Germany's largest donation platform — transparent, with a tax receipt. Every amount counts: M.E. research is so underfunded that even small sums make a difference.",
    supportButton: 'Donate Now',
  },
  makeDifference: {
    title: 'Join in',
  },
  joinUs: {
    title: 'Run with us',
    subtitle: 'Any run, any distance.',
    description: 'Running a race from our list, or a completely different one? Playing tennis, doing a triathlon, or just jogging on Sundays? Perfect. Sign up, we send you your shirt and add your run to our list.',
    linkText: 'Go to the Get Involved page',
    contactInfo: 'Or email us directly: runforme.contact(at)gmail.com',
    floatingLabel: 'Run with us',
  },
};

export const RUNNING_EVENTS: RunningEvent[] = [
  {
    date: 'March 8, 2026',
    name: 'Bienwald Marathon Kandel',
    location: 'Kandel, Germany',
  },
  {
    date: 'March 15, 2026',
    name: 'Mammuthmarsch Munich',
    location: 'Munich, Germany',
  },
  {
    date: 'March 22, 2026',
    name: 'Rome Marathon',
    location: 'Rome, Italy',
  },
  {
    date: 'March 29, 2026',
    name: 'Berlin Half Marathon',
    location: 'Berlin, Germany',
  },
  {
    date: 'April 12, 2026',
    name: 'ADAC Marathon Hannover',
    location: 'Hannover, Germany',
    distances: 'Marathon | Half Marathon | Relay | 10k',
  },
  {
    date: 'April 18, 2026',
    name: 'Leipzig Marathon',
    location: 'Leipzig, Germany',
    distances: 'Marathon | Half Marathon | 4k & 10k Run/Walk',
  },
  {
    date: 'April 19, 2026',
    name: 'Deutsche Post Marathon Bonn',
    location: 'Bonn, Germany',
    distances: 'Marathon | Half Marathon | Relay | 10k',
  },
  {
    date: 'April 19, 2026',
    name: 'Vienna City Marathon',
    location: 'Vienna, Austria',
    distances: 'Marathon | Half Marathon | Relay | 10k',
  },
  {
    date: 'April 26, 2026',
    name: 'Dusseldorf Marathon',
    location: 'Dusseldorf, Germany',
    distances: 'Marathon | Half Marathon | Relay',
  },
  {
    date: 'April 26, 2026',
    name: 'Heidelberg Half Marathon',
    location: 'Heidelberg, Germany',
  },
  {
    date: 'April 26, 2026',
    name: 'Hamburg Marathon',
    location: 'Hamburg, Germany',
    distances: 'Marathon | Half Marathon | Relay',
  },
  {
    date: 'May 2, 2026',
    name: 'Traunstein',
    location: 'Traunstein, Germany',
    distances: '12k | Half Marathon',
  },
  {
    date: 'May 9, 2026',
    name: 'Gutenberg Half Marathon Mainz',
    location: 'Mainz, Germany',
    distances: 'Half Marathon',
  },
  {
    date: 'May 10, 2026',
    name: 'Copenhagen Marathon',
    location: 'Copenhagen, Denmark',
  },
  {
    date: 'May 13, 2026',
    name: 'ASV Nachtlauf',
    location: 'Cologne, Germany',
  },
  {
    date: 'May 17, 2026',
    name: 'Stuttgart Marathon',
    location: 'Stuttgart, Germany',
    distances: '5k | 10k | Half Marathon | Relay',
  },
  {
    date: 'May 25, 2026',
    name: 'Royal Run Copenhagen',
    location: 'Copenhagen, Denmark',
    distances: '5k | 10k',
  },
  {
    date: 'June 7, 2026',
    name: 'ProPotsdam Schlösserlauf',
    location: 'Potsdam, Germany',
    distances: '10k | Half Marathon',
  },
  {
    date: 'June 10, 2026',
    name: 'Unilauf Cologne',
    location: 'Cologne, Germany',
    distances: '2.5k | 5k | 10k',
  },
  {
    date: 'June 13, 2026',
    name: 'Tschirgant Skyrun',
    location: 'Imst, Austria',
    distances: 'Trail run',
  },
  {
    date: 'June 19, 2026',
    name: 'Mittsommerlauf Wallau',
    location: 'Wallau, Germany',
  },
  {
    date: 'June 28, 2026',
    name: 'Hamburg Half Marathon',
    location: 'Hamburg, Germany',
  },
  {
    date: 'June 28, 2026',
    name: 'Trier Marathon',
    location: 'Trier, Germany',
    distances: 'Marathon',
  },
  {
    date: 'July 1, 2026',
    name: 'Lousberglauf Aachen',
    location: 'Aachen, Germany',
  },
  {
    date: 'July 5, 2026',
    name: 'Munich Sportscheck Run',
    location: 'Munich, Germany',
    distances: '5k | 10k | Half Marathon',
  },
  {
    date: 'July 11, 2026',
    name: 'Rosengarten Skymarathon',
    location: 'South Tyrol, Italy',
    distances: 'Skymarathon',
  },
  {
    date: 'July 11, 2026',
    name: '7-Täler Lauf Dietfurt',
    location: 'Dietfurt, Germany',
  },
  {
    date: 'July 15, 2026',
    name: 'Firmenlauf Friedrichshafen',
    location: 'Friedrichshafen, Germany',
  },
  {
    date: 'August 1, 2026',
    name: 'Adidas Runners City Night',
    location: 'Berlin, Germany',
    distances: '5k | 10k',
  },
  {
    date: 'September 5, 2026',
    name: 'Mammut Marsch Bremen',
    location: 'Bremen, Germany',
  },
  {
    date: 'September 6, 2026',
    name: 'Carglass Triathlon Köln',
    location: 'Cologne, Germany',
    distances: 'Triathlon',
  },
  {
    date: 'September 12, 2026',
    name: 'WE&ME SeeStadtlauf',
    location: 'Vienna, Austria',
    distances: '5k | 10k',
  },
  {
    date: 'September 12, 2026',
    name: 'USee Run 2026',
    location: 'Düsseldorf, Germany',
  },
  {
    date: 'September 13, 2026',
    name: 'Münster Marathon',
    location: 'Münster, Germany',
    distances: 'Marathon',
  },
  {
    date: 'September 20, 2026',
    name: 'Baden Marathon Karlsruhe',
    location: 'Karlsruhe, Germany',
    distances: 'Marathon | Half Marathon | Relay',
  },
  {
    date: 'October 10, 2026',
    name: 'Cologne Marathon',
    location: 'Cologne, Germany',
    distances: 'Marathon | Half Marathon | Relay',
  },
  {
    date: 'October 11, 2026',
    name: 'Monopteros Run Munich',
    location: 'Munich, Germany',
    distances: '5-33 km',
  },
  {
    date: 'October 11, 2026',
    name: 'Munich Marathon',
    location: 'Munich, Germany',
    distances: 'Marathon | Half Marathon | Relay',
  },
  {
    date: 'October 25, 2026',
    name: 'Frankfurt Marathon',
    location: 'Frankfurt, Germany',
    distances: 'Marathon | Relay',
  },
  {
    date: 'November 1, 2026',
    name: 'New York City Marathon',
    location: 'New York, USA',
  },
  {
    date: 'November 8, 2026',
    name: 'Athens Marathon',
    location: 'Athens, Greece',
    distances: 'Marathon',
  },
  // 2025 – already run
  {
    date: 'October 2025',
    name: 'Cologne Marathon',
    location: 'Cologne, Germany',
    distances: 'Marathon',
  },
  {
    date: 'September 2025',
    name: 'Berlin Marathon',
    location: 'Berlin, Germany',
    distances: 'Marathon',
  },
  {
    date: 'October 2025',
    name: 'Lisbon Marathon',
    location: 'Lisbon, Portugal',
    distances: 'Marathon',
  },
];

// Running events page specific content
export const RUNNING_EVENTS_PAGE = {
  title: 'Runs',
  upcomingTitle: 'Runs in 2026',
  pastTitle: 'Runs in 2025',
  subtitle: 'We run to raise awareness and funds for M.E. research and patient care',
  description: 'These are the runs coming up in 2026. Pick one — or bring your own.',
  callToAction: {
    title: 'Run with us',
    description: 'Running a race from this list, or a completely different one? Sign up and we will send you your shirt.',
    additionalInfo: 'Marathon, 5k, trail run or a lap around the park: every distance counts, and every run goes on our list. We would love to have you.',
    contactInfo: 'Or email us directly: runforme.contact(at)gmail.com',
  },
};

export const ABOUT_US = {
  title: 'About Us',
  // Content from FundraiserInfo is displayed also on this page
  team: {
    title: 'Our Team',
    imageAlt: 'Our Team - Rebecca, Valentina, and Felicitas',
    description: 'Our dedicated runners committed to raise M.E. funds and awareness.',
    members: [
      {
        name: 'Rebecca',
        role: 'Partnerships',
        description: 'Rebecca builds collaborations and partnerships so our message reaches more people.',
        imageSrc: '/Rebecca.JPG',
        imageAlt: 'Portrait of Rebecca',
      },
      {
        name: 'Valentina',
        role: 'Design',
        description: 'Valentina creates the visual identity and design materials for Run for M.E.',
        imageSrc: '/Vale.png',
        imageAlt: 'Portrait of Valentina',
      },
      {
        name: 'Felicitas',
        role: 'Instagram',
        description: 'Felicitas manages our Instagram presence and shares updates and awareness content.',
        imageSrc: '/Lici.png',
        imageAlt: 'Portrait of Lici',
      },
    ],
  },
  joinOurCause: {
    title: 'Become part of it',
    description: 'We are looking for people to line up in our shirt — at a run from our list, or at one of your own.',
    additionalInfo: 'Whether you run, donate, share our story or bring an idea of your own: all of it helps make M.E. more visible. Just get in touch.',
  },
};

// Mitmachen (Get Involved) page – contact and join
export const MITMACHEN = {
  title: 'Run With Us & Get a Shirt',
  subtitle:
    'More than 300 people already run in our shirt. Sign up and we send you yours for €14 at cost price — and add your run to our list. Any distance.',
  email: {
    label: 'Email',
    address: 'runforme.contact(at)gmail.com',
    mailto: 'runforme.contact@gmail.com',
    description: 'Send us a message for questions about runs, partnerships, or how to join.',
  },
  instagram: {
    title: 'Follow us on Instagram',
    description: 'Training updates, M.E. awareness content, and behind-the-scenes moments.',
    button: 'Follow @runformecfs',
  },
  form: {
    title: 'Register to Join',
    infoTitle: 'How it works',
    infoText: [
      'We are looking for runners or sport enthusiasts worldwide – any race, any distance or sport event – who want to run in our T-shirt and raise awareness for M.E.. We will add your run to our list on the website, and with your permission share photos of you and your run on Instagram. The motto: the more sport enthusiasts join and spread the word, the better.',
      'Of course, T-shirts can also be ordered without registering for official runs. In the spirit of “Move for M.E.” we are happy if you wear our T-shirts at park runs, tennis tournaments, or other events of any kind!',
      'We send every participant our T-shirt as well as flyers for €14 (€10 production + €2 shipping within Germany + 2€ further material costs), and ask you to share the project in your network to find more sport enthusiasts as well as raise awareness for M.E..',
      'For larger events or ideas for other actions, we are also very open.Donations for M.E. research and support are collected additionally via the link on our website for the listed projects.',
      'If you want to order more T-shirts or have other ideas for actions, just email us: runforme.contact(at)gmail.com.',

    ],
    paymentInfo: 'Payment is made via PayPal before shipping. T-shirts are reordered regularly — delivery times may vary depending on stock. Feel free to reach out if you have any questions.',
    material: 'Material: performance T-shirt, 100% polyester, unisex fit.',
    fields: {
      name: 'Full name',
      email: 'Email address',
      phone: 'Phone number (optional – for our WhatsApp group with all runners)',
      address: 'Postal address for T-shirt delivery',
      run: 'Which race/run or sport event are you participating in? (optional)',
      runDate: 'Date of the run / When do you need the T-shirt? (optional)',
      size: 'T-shirt size (Unisex)',
      howHeard: 'How did you hear about us? (optional)',
      notes: 'Notes about your participation (optional)',
    },
    sizeOptions: ['S', 'M', 'L', 'XL', 'XXL', 'XXXL'],
    outOfStock: [],
    outOfStockLabel: 'longer delivery time',
    outOfStockHelper: 'Note: this size is currently not in stock — we can still take your order, but delivery may take a bit longer.',
    infoShowMore: 'Read more',
    infoShowLess: 'Show less',
    consentText:
      'We process your data in order to handle your order, ship the T-shirt and contact you about your run.',
    consentLinkText: 'More on this in our Privacy Policy',
    withdrawal: {
      title: 'Your right of withdrawal',
      text: 'You can withdraw from your order within 14 days of receiving the T-shirt without giving any reason — an informal email to runforme.contact(at)gmail.com is enough. We will then refund the full amount including shipping costs; you bear the cost of returning the shirt.',
      linkText: 'Full withdrawal policy in the Legal Notice',
    },
    submitHint: 'This orders your T-shirt for €14 and makes you part of the community.',
    submit: 'Order with obligation to pay',
    submitting: 'Ordering…',
    success: {
      title: 'Order received!',
      text: 'Thank you for your order at Run for M.E.! We will be in touch soon and ship your T-shirt and flyer.',
    },
    error: 'Something went wrong. Please try again or contact us at runforme.contact@gmail.com.',
    errors: {
      required: 'This field is required.',
      email: 'Please enter a valid email address.',
    },
  },
};

export const SOCIAL_MEDIA = {
  instagram: {
    handle: '@runformecfs',
    url: 'https://www.instagram.com/runformecfs/',
    description: 'Follow our journey on Instagram: runs, shirts in the wild, M.E. awareness and behind-the-scenes moments.',
  },
  // Instagram page specific content (used on Mitmachen page)
  page: {
    title: 'Follow Our Journey',
    subtitle: 'Runs, shirts in the wild and M.E. awareness — straight from the community.',
    feed: {
      title: 'Instagram Feed',
      description: 'Our latest posts and updates from Instagram',
      placeholder: {
        title: 'Instagram Feed',
        description: 'Follow us on Instagram to see where our shirt is out running right now.',
        button: 'View Instagram Profile',
      },
    },
    callToAction: {
      title: 'Stay Connected',
      description: 'On Instagram we post our community\'s runs, finish-line photos and everything around M.E. awareness.',
      additionalInfo: 'Share our posts — every like, comment and share puts M.E. in front of more eyes. That is the whole point.',
      button: 'Follow @runformecfs',
    },
  },
};

export const DONATION_INFO = {
    title: 'Donate for M.E.',
    subtitle: 'Your donation goes straight to M.E. research and care for patients',
    mainDonationButton: {
        title: 'Donate Now',
        description: 'Support our running fundraiser for M.E. research and patient support',
        url: 'https://www.betterplace.org/de/fundraising-events/55788-run-for-me-cfs',
    },
    betterplaceDisclaimer: {
        title: 'Our Cooperation with betterplace.org',
        text: 'We cooperate with the non-profit platform betterplace.org. Betterplace.org is Germany\'s largest donation platform and ensures transparency and security of donations. It also enables us to issue a donation receipt.',
    },
    impactSection: {
        title: 'Why Your Support Matters',
        cards: [
        {
            title: 'Most Disabling Chronic Illness',
            description: 'M.E. is considered one of the most disabling chronic illnesses, with quality of life worse than many other conditions affecting 71 million people worldwide¹⁴.',
        },
        {
            title: 'Research Funding Gap',
            description: 'Current research funding in Germany is only around 15 million euros for a 3-year M.E. research program⁶ - a fraction of what other chronic illnesses receive.',
        },
        {
            title: 'Economic Impact',
            description: 'M.E. (and Long COVID) costs Germany approximately 1.5% of GDP annually² - a devastating economic burden that affects everyone.',
            },
        ],
    },
    // Donation call to action in Fundraiser Info page section
    learnMore: {
        title: 'Learn More About M.E.',
        subtitle: 'Want to understand more about what living with M.E. means?',
        button: 'Visit M.E. Information Page',
    },
    causes: [
    {
      title: 'Advancing M.E. Research',
      description: "Supporting the ME Research Foundation in funding new research projects to advance understanding and treatment of M.E..",
      details: 'Your donations will fund research into biomarkers and mechanisms of M.E. The research aims to identify diagnostic tools and develop evidence-based treatments for this complex neuroimmunological illness.',
    },
    {
      title: 'Virtual medical care',
      description: 'Providing virtual consultation services for severely ill M.E. patients who cannot access traditional medical care.',
      details: 'This project from the Lost Voices Foundation provides specialized virtual medical consultations for bedridden patients, including symptom management, medication reviews, and coordination with local healthcare providers.',
    },
  ],
};

// Legal content for English only
export const LEGAL_CONTENT = {
  impressum: {
    title: 'Legal Notice',
    contact: {
      title: 'Contact Information',
      description: 'This website is operated by a private individual as part of a personal awareness and fundraising initiative (information pursuant to § 5 DDG).',
    },
    purpose: {
      title: 'Purpose of the Website',
      description: 'This website serves to raise awareness of M.E./CFS, to direct donations to the research projects listed here, and to distribute our T-shirts at cost price. This is a private initiative without any intention to make a profit, and not a registered organization or charitable institution.',
      goal: 'The goal is to collect donations for research into Myalgic Encephalomyelitis/Chronic Fatigue Syndrome (M.E./CFS) and to raise awareness about this condition.',
    },
    shirts: {
      title: 'T-Shirts at Cost Price',
      description: 'Anyone who signs up via the form on the “Join in” page receives a T-shirt and flyers from us at a cost price of €14 (€10 production, €2 shipping within Germany, €2 further materials). We make no profit on this — the amount covers only the costs we incur. Even though we act without any intention to make a profit, statutory consumer rights apply to these orders; the following information applies to all orders placed by consumers.',
      price: {
        title: 'Price and payment:',
        text: 'The stated price of €14 is a total price including shipping within Germany. VAT is not shown separately. Payment is made via PayPal before dispatch. Different shipping costs apply for delivery outside Germany — please contact us by email in advance.',
      },
      contract: {
        title: 'Conclusion of contract and delivery:',
        text: 'By submitting the sign-up form you place a binding order. The contract is concluded when we confirm it by email. T-shirts are reordered regularly; depending on stock, delivery times may vary. We do not store or publish the contract text separately; you will receive your order details by email.',
      },
      withdrawal: {
        title: 'Right of withdrawal:',
        text: 'You have the right to withdraw from this contract within fourteen days without giving any reason. The withdrawal period expires fourteen days from the day on which you, or a third party other than the carrier indicated by you, acquires physical possession of the goods. To exercise your right of withdrawal, you must inform us (contact details above) of your decision to withdraw from this contract by an unequivocal statement — for example, by email. To meet the withdrawal deadline, it is sufficient for you to send your communication concerning the exercise of the right of withdrawal before the withdrawal period has expired.',
      },
      withdrawalConsequences: {
        title: 'Effects of withdrawal:',
        text: 'If you withdraw from this contract, we shall reimburse all payments received from you, including the costs of delivery, without undue delay and no later than fourteen days from the day on which we are informed about your decision to withdraw. We may withhold reimbursement until we have received the goods back or you have supplied evidence of having sent back the goods, whichever is the earliest. You shall send back the goods without undue delay and in any event no later than fourteen days from the day on which you communicate your withdrawal to us. You will have to bear the direct cost of returning the goods. You are only liable for any diminished value of the goods resulting from handling other than what is necessary to establish the nature, characteristics and functioning of the goods.',
      },
      warranty: {
        title: 'Warranty:',
        text: 'Statutory warranty rights apply (§§ 434 ff. of the German Civil Code). If there is a defect, please contact us by email — we will replace the T-shirt or refund the amount.',
      },
      dispute: {
        title: 'Dispute resolution:',
        text: 'We are neither willing nor obliged to participate in dispute resolution proceedings before a consumer arbitration board.',
      },
    },
    disclaimer: {
      title: 'Disclaimer',
      content: {
        title: 'Content Responsibility:',
        text: 'The information provided serves exclusively for education and fundraising purposes. No guarantee is given for the accuracy of medical information. For medical questions, please consult qualified doctors.',
      },
      external: {
        title: 'External Links:',
        text: 'This website contains links to external websites. The respective providers are responsible for the content of these external websites. We have no influence on their content and assume no liability for it.',
      },
      donations: {
        title: 'Donations:',
        text: 'All donations are forwarded directly to the specified research organizations. We are not responsible for the use of donations by these organizations or their research results.',
      },
    },
    copyright: {
      title: 'Copyright',
      description: 'The content of this website is protected by copyright. The use of texts, images, or other content is only permitted with express consent.',
      images: 'Image Sources: All images used come from royalty-free sources or have been used with appropriate rights.',
    },
    additional: {
      title: 'Additional Legal Information',
      links: 'Privacy Policy | Cookie Policy',
    },
  },
  privacy: {
    title: 'Privacy Policy',
    responsible: {
      title: '1. Data Controller',
      description: 'Responsible for data processing on this website:',
    },
    general: {
      title: '2. General Information on Data Processing',
      description: 'This website serves to raise awareness of M.E./CFS, to direct donations to research projects, and to distribute our T-shirts at cost price. It is a private initiative without any intention to make a profit.',
      principle: 'Principle: We only collect and process data that is necessary to operate the website, to handle your order, and — where you have given consent — to measure reach. No profiling takes place and no data is passed on for advertising purposes.',
    },
    automatic: {
      title: '3. Automatically Collected Data',
      server: {
        title: 'Server Log Files:',
        description: 'This website is hosted by Netlify (Netlify, Inc., 512 2nd Street, San Francisco, CA 94107, USA). The following data is automatically recorded in server log files with each access:',
        data: [
          'IP address of the accessing computer',
          'Date and time of access',
          'Name and URL of the retrieved file',
          'Amount of data transferred',
          'Message about successful retrieval',
          'Browser type and version',
          'Operating system of the user',
        ],
      },
      analytics: {
        title: 'Google Analytics:',
        description: 'This website uses Google Analytics 4 (Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Ireland) to measure reach. Google Analytics is loaded only after you have given consent in the cookie banner — without your consent no analysis takes place at all. Google Signals and ad personalisation are disabled, and IP addresses are truncated and not stored. A transfer to Google LLC in the USA cannot be ruled out; Google is certified under the EU-US Data Privacy Framework. The following data is processed:',
        data: [
          'IP address (anonymized)',
          'Pages visited and time spent',
          'Referrer source (how you came to the website)',
          'Browser and device information',
          'Geographic location data (country/city)',
        ],
        legal: 'Legal Basis: Art. 6 para. 1 lit. a GDPR (consent)',
        retention: 'Retention Period: 26 months (Google Analytics standard)',
        optout: 'Opt-out: You can disable Google Analytics through browser settings or Google Analytics Opt-out Browser Add-on.',
      },
      legal: 'Legal Basis: Art. 6 para. 1 lit. f GDPR (legitimate interest in the technical functionality and security of the website). Processing also takes place in the USA; Netlify is certified under the EU-US Data Privacy Framework and we have concluded a data processing agreement including EU standard contractual clauses with Netlify.',
      retention: 'Retention Period: Netlify retains the log files only briefly to ensure operation and deletes them automatically. We do not evaluate them on a personal basis.',
    },
    cookies: {
      title: '4. Cookies',
      necessary: {
        title: 'Settings in local storage:',
        description: 'This website stores your language choice and your cookie decision in your browser’s local storage (localStorage, entries “language” and “cookieConsent”). These entries are only written after you have agreed in the cookie banner, remain solely on your device and are not transmitted to us.',
      },
      analytics: {
        title: 'Analytics Cookies (Google Analytics):',
        description: 'After you have given consent, Google Analytics sets cookies to analyse use of the website. Without your consent, Google Analytics is not loaded and no analytics cookies are set.',
        legal: 'Legal Basis: Art. 6 para. 1 lit. a GDPR and § 25 para. 1 TDDDG (consent)',
        optout: 'Opt-out: You can withdraw your consent at any time with future effect in the cookie settings.',
      },
      tracking: {
        title: 'No Marketing Cookies:',
        description: 'No marketing or advertising cookies are used. No profiling and no cross-device user tracking takes place; Google Signals and ad personalisation are disabled.',
      },
      legal: 'Legal Basis: For storage in local storage and for analytics cookies, Art. 6 para. 1 lit. a GDPR in conjunction with § 25 para. 1 TDDDG (consent).',
    },
    external: {
      title: '5. External Links and Services',
      forms: {
        title: 'Registration Form (Netlify Forms and Google Sheets)',
        description:
          'When you submit the registration form (“Get Involved”), we process the data you enter (name, email address, postal address, phone number where given, run, T-shirt size, notes) in order to handle your order, ship the T-shirt and add your run to our list. The submission is stored by Netlify (Netlify Forms) and additionally transferred into a spreadsheet at Google (Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Ireland), which we keep as our internal participant list. No further disclosure to third parties takes place — with the exception of the shipping service provider, which needs your address for delivery.',
        legal:
          'Legal Basis: Art. 6 para. 1 lit. b GDPR (performance of the purchase contract and pre-contractual measures). Providing your name and address is necessary for shipping; without this information we cannot fulfil the order. The remaining fields are optional.',
        retention:
          'Retention Period: We keep submissions as long as necessary for processing, shipping and follow-up questions. Where we are required to retain records, we keep the relevant data until the statutory tax retention periods expire (§ 147 German Fiscal Code) and delete it afterwards.',
      },
      payment: {
        title: 'Payment (PayPal):',
        description:
          'T-shirts are paid for via PayPal (PayPal (Europe) S.à r.l. et Cie, S.C.A., 22-24 Boulevard Royal, 2449 Luxembourg). PayPal processes your payment data under its own responsibility; from PayPal we only receive confirmation that payment has been received, together with the name you hold there. PayPal’s privacy policy applies.',
        legal: 'Legal Basis: Art. 6 para. 1 lit. b GDPR (performance of the purchase contract).',
      },
      donations: {
        title: 'Donation Links:',
        description: 'This website contains links to external donation platforms and research organizations. These external providers are responsible for processing your data on their websites. Please note their respective privacy policies.',
      },
      social: {
        title: 'Social Media:',
        description: 'Links to social media lead to external platforms. The privacy policies of these platforms apply to the use of their services.',
      },
    },
    rights: {
      title: '6. Your Rights as a Data Subject',
      description: 'You have the following rights under the GDPR:',
      list: [
        'Right of access (Art. 15 GDPR)',
        'Right to rectification (Art. 16 GDPR)',
        'Right to erasure (Art. 17 GDPR)',
        'Right to restriction of processing (Art. 18 GDPR)',
        'Right to data portability (Art. 20 GDPR)',
        'Right to object (Art. 21 GDPR)',
        'Right to withdraw consent at any time with future effect (Art. 7 para. 3 GDPR)',
      ],
      contact: 'Contact: To exercise your rights, please contact us at the email address provided above. No automated decision-making, including profiling, within the meaning of Art. 22 GDPR takes place.',
    },
    complaint: {
      title: '7. Right to Lodge a Complaint with the Supervisory Authority',
      description: 'You have the right to lodge a complaint with a data protection supervisory authority regarding the processing of your personal data. The supervisory authority competent for us is:',
      authority: 'Bavarian State Office for Data Protection Supervision (BayLDA)',
      address: 'Promenade 27',
      city: '91522 Ansbach',
      email: 'Email: poststelle(at)lda.bayern.de',
    },
    changes: {
      title: '8. Changes to this Privacy Policy',
      description: 'We reserve the right to adapt this privacy policy to changed legal situations or changes to our website. The current version is available on this website.',
    },
  },
  cookies: {
    title: 'Cookie Policy',
    what: {
      title: 'What are Cookies?',
      description: 'Cookies are small text files that are stored on your computer or mobile device when you visit a website. They help improve the website and provide you with a better user experience.',
    },
    which: {
      title: 'Which Cookies Does This Website Use?',
      preferences: {
        title: 'Settings in local storage:',
        description: 'This website stores your language choice and your cookie decision in your browser’s local storage (localStorage). These entries are only written with your explicit consent, remain on your device and are not transmitted to us. This website sets no cookies of its own.',
      },
      analytics: {
        title: 'Analytics Cookies (Google Analytics):',
        description: 'After you have given consent, Google Analytics sets cookies to analyse use of the website. Without your consent, Google Analytics is not loaded and no analytics cookies are set.',
      },
      tracking: {
        title: 'No Marketing Cookies:',
        description: 'No marketing or tracking cookies are used. No profiling or user tracking takes place.',
      },
    },
    detailed: {
      title: 'Detailed Cookie List',
      preferences: {
        title: 'Entries in local storage',
        language: {
          title: 'Language Preference (localStorage)',
          purpose: 'Purpose: Stores your preferred language (English or German) for the website',
          duration: 'Storage Duration: Until manually deleted or browser data is cleared',
          legal: 'Legal Basis: Art. 6 para. 1 lit. a GDPR in conjunction with § 25 para. 1 TDDDG (consent)',
          optout: 'Opt-out: You can change language in the header or clear browser data. If you decline cookies, language preference will not be saved.',
        },
        consent: {
          title: 'Cookie Consent (localStorage)',
          purpose: 'Purpose: Remembers your cookie consent choice to avoid showing the banner repeatedly',
          duration: 'Storage Duration: Until manually deleted or browser data is cleared',
          legal: 'Legal Basis: Art. 6 para. 1 lit. a GDPR in conjunction with § 25 para. 1 TDDDG (consent)',
          optout: 'Opt-out: You can clear browser data to reset the choice. If you decline cookies, no consent data is stored.',
        },
      },
      analytics: {
        title: 'Analytics Cookies',
        googleAnalytics: {
          title: 'Analytics Cookies (Google Analytics)',
          purpose: 'Purpose: Analyzes website usage to understand how visitors use our website',
          duration: 'Storage Duration: 26 months (Google Analytics standard)',
          legal: 'Legal Basis: Art. 6 para. 1 lit. a GDPR in conjunction with § 25 para. 1 TDDDG (consent)',
          optout: 'Opt-out: You can withdraw your consent at any time in the cookie settings.',
        },
      },
    },
    decline: {
      title: 'What Happens If You Decline Cookies?',
      description: 'If you decline cookies, we will not store any data in your browser\'s local storage. This means:',
      effects: [
        'Your language preference will not be saved and will reset to English each time you visit',
        'The cookie consent banner will appear again on your next visit',
        'You can still use all website features normally',
        'No personal data is collected or stored',
      ],
      note: 'Note: If you decline, neither cookies nor local storage entries are created. The website works fully — it simply does not remember anything from visit to visit.',
    },
    manage: {
      title: 'How Can You Manage Cookies?',
      browser: {
        title: 'Browser Settings:',
        description: 'You can manage cookies and local storage in your browser settings. There you can delete cookies or restrict cookie storage.',
      },
      note: {
        title: 'Note:',
        description: 'Disabling technically necessary cookies may cause this website to no longer function properly. Language preferences and cookie consent will be reset.',
      },
      instructions: {
        title: 'Instructions for Common Browsers:',
        list: [
          'Chrome: Settings → Privacy and Security → Cookies and other site data',
          'Firefox: Settings → Privacy & Security → Cookies and Site Data',
          'Safari: Settings → Privacy → Cookies and Website Data',
          'Edge: Settings → Cookies and site permissions → Cookies',
        ],
      },
    },
    external: {
      title: 'External Cookies',
      donations: {
        title: 'Donation Links:',
        description: 'When you click on links to external donation platforms, these websites may set their own cookies. These are subject to the privacy policies of the respective providers.',
      },
      social: {
        title: 'Social Media:',
        description: 'Links to social media may also set cookies when you use these services.',
      },
    },
    contact: {
      title: 'Contact',
      description: 'If you have questions about cookies or data processing on this website, please feel free to contact us:',
      email: 'Email: runforme.contact(at)gmail.com',
    },
  },
  cookieBanner: {
    title: 'Cookie Settings',
    description: 'This website uses cookies to ensure proper functionality and to analyze website usage. You can adjust your settings.',
    tracking: 'For detailed information about cookies and data processing, please see our',
    cookiePolicy: 'Cookie Policy',
    privacyPolicy: 'Privacy Policy',
    settings: 'Settings',
    acceptAll: 'Accept All',
  },
};

export const CONTENT = {
  meInfo: ME_INFO,
  fundraiserInfo: FUNDRAISER_INFO,
  runningEvents: RUNNING_EVENTS,
  runningEventsPage: RUNNING_EVENTS_PAGE,
  aboutUs: ABOUT_US,
  socialMedia: SOCIAL_MEDIA,
  mitmachen: MITMACHEN,
  donationInfo: DONATION_INFO,
  legal: LEGAL_CONTENT,
  citations: CITATIONS,
};
